// ========================================
// Formation Service
// Handles all formation-related operations
// ========================================

const FormationService = {
    /**
     * Get all formations
     */
    getFormations() {
        const formations = DataManager.getAll(DataManager.KEYS.FORMATIONS);
        return formations.sort((a, b) => (a.order || 0) - (b.order || 0));
    },

    /**
     * Get formation by ID
     */
    getFormation(id) {
        return DataManager.getById(DataManager.KEYS.FORMATIONS, id);
    },

    /**
     * Get formations by type
     */
    getFormationsByType(type) {
        return this.getFormations().filter(f => f.type === type);
    },

    /**
     * Create new formation
     */
    createFormation(data) {
        const formation = {
            id: Utils.generateUUID(),
            name: data.name,
            type: data.type,
            commercialName: data.commercialName || null,
            studyPlanId: data.studyPlanId || null,
            academicYear: data.academicYear || null,
            description: data.description || '',
            order: data.order || this.getFormations().length, // Default to end of list
            defaultStartTime: data.defaultStartTime || null,
            defaultEndTime: data.defaultEndTime || null,
            enrollmentOpen: data.enrollmentOpen === true,
            createdAt: new Date().toISOString()
        };
        return DataManager.add(DataManager.KEYS.FORMATIONS, formation);
    },

    /**
     * Update formation
     */
    updateFormation(id, data) {
        return DataManager.update(DataManager.KEYS.FORMATIONS, id, data);
    },

    /**
     * Delete formation and all related data
     */
    deleteFormation(id) {
        // Delete all subjects in this formation
        const subjects = DataManager.getAll(DataManager.KEYS.SUBJECTS)
            .filter(s => s.formationId === id);

        subjects.forEach(subject => {
            SubjectService.deleteSubject(subject.id);
        });

        return DataManager.delete(DataManager.KEYS.FORMATIONS, id);
    },

    /**
     * Update order of multiple formations
     * @param {string[]} formationIds - Array of formation IDs in the new order
     */
    updateFormationsOrder(formationIds) {
        const formations = this.getFormations();
        const updates = [];

        formationIds.forEach((id, index) => {
            const formation = formations.find(f => f.id === id);
            if (formation && formation.order !== index) {
                formation.order = index;
                updates.push(formation);
            }
        });

        if (updates.length > 0) {
            DataManager.saveAll(DataManager.KEYS.FORMATIONS, formations);
        }
        return true;
    },

    /**
     * Get formation with all its subjects
     */
    getFormationWithSubjects(id) {
        const formation = this.getFormation(id);
        if (!formation) return null;

        const subjects = DataManager.getAll(DataManager.KEYS.SUBJECTS)
            .filter(s => s.formationId === id);

        return {
            ...formation,
            subjects
        };
    },

    /**
     * Get formation statistics
     */
    getFormationStats(formationId) {
        const subjects = DataManager.getAll(DataManager.KEYS.SUBJECTS)
            .filter(s => s.formationId === formationId);
        const classes = DataManager.getAll(DataManager.KEYS.CLASSES)
            .filter(c => c.formationId === formationId);

        let totalWeeklyHours = 0;
        classes.forEach(cls => {
            const duration = Utils.calculateDuration(cls.startTime, cls.endTime);
            totalWeeklyHours += duration;
        });

        // Get unique professors
        const professorIds = new Set(classes.map(c => c.professorId));

        return {
            totalSubjects: subjects.length,
            totalClasses: classes.length,
            totalProfessors: professorIds.size,
            totalWeeklyHours
        };
    },

    /**
     * Get formation type label in Spanish
     */
    // Type names are school data (Plan de Estudios → Tipos).
    getFormationTypeLabel(type) {
        return typeof FormationTypeService !== 'undefined' ? FormationTypeService.name(type) : type;
    }
};

// Make FormationService globally available
window.FormationService = FormationService;
